import React, { useCallback, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { ChatNewPayload } from '@atlaskit/rovo-triggers/types';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';

export type AgentMentionContext = Exclude<NonNullable<ChatNewPayload['data']['prompt']>, string>;

const messages = defineMessages({
	agentMentionNudgeTitle: {
		id: 'page-rovo-config-common.agent-mention-nudge-title',
		defaultMessage: 'Start chatting with agent',
		description:
			'Title for the agent mention nudge that appears next to the Rovo button when a user inserts an agent mention in the document',
	},
	agentMentionNudgeTitleWithAgentName: {
		id: 'page-rovo-config-common.agent-mention-nudge-title-with-agent-name.ai-non-final',
		defaultMessage: 'Start chatting with {agentName}',
		description:
			'Title for the agent mention nudge that appears next to the Rovo button when a user inserts an agent mention in the document. The placeholder {agentName} is replaced with the display name of the mentioned agent.',
	},
	agentMentionNudgeTitle2: {
		id: 'page-rovo-config-common.agent-mention-nudge-title-2',
		defaultMessage: 'Give agent a task?',
		description:
			'Title for the agent mention nudge that appears next to the Rovo button when a user inserts an agent mention in the document',
	},
	agentMentionNudgeTitleWithAgentName2: {
		id: 'page-rovo-config-common.agent-mention-nudge-title-with-agent-name.ai-non-final-2',
		defaultMessage: 'Give {agentName} a task?',
		description:
			'Title for the agent mention nudge that appears next to the Rovo button when a user inserts an agent mention in the document. The placeholder {agentName} is replaced with the display name of the mentioned agent.',
	},
	agentMentionNudgeIconLabel: {
		id: 'page-rovo-config-common.agent-mention-nudge-icon-label',
		defaultMessage: 'Agent icon',
		description: 'Accessible label for the icon in the agent mention nudge',
	},
	agentMentionNudgeSecondaryTitle: {
		id: 'page-rovo-config-common.agent-mention-nudge-secondary-title',
		defaultMessage: 'Start a conversation using this agent',
		description: 'Subtitle shown below the agent mention nudge title on hover',
	},
});

export const AGENT_MENTION_NUDGE_ID = 'agent-mention-nudge';

/** ProseMirror node type for task list items. */
export const TASK_ITEM_NODE_TYPE = 'taskItem';

/** Defined outside the hook so React keeps the same DOM node across re-renders. */
const AgentMentionIcon = ({ label }: { label: string }) => (
	<AiChatIcon size="small" label={label} />
);

/**
 * Returns true only when the active element is inside the main page editor,
 * excluding comment editors, AI modals and the Rovo prompt screen.
 *
 * Mirrors isMainEditorContent from @confluence/editor-presets — kept local to
 * avoid adding editor-presets as a dependency of this package.
 */
const isMainEditorContent = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}
	if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
		return false;
	}
	if (target.closest('[data-testid="ak-editor-plugin-ai-modal"]')) {
		return false;
	}
	if (target.closest('[data-testid^="ak-editor-ai"]')) {
		return false;
	}
	if (target.closest('[data-testid="rovo-prompt-session-screen"]')) {
		return false;
	}
	if (target.closest('[data-testid="chromeless-editor"]')) {
		return false;
	}
	return target.closest('.ProseMirror') !== null;
};

/**
 * Hook that creates the agent mention nudge action.
 *
 * @param agentId - Resolved Agent Studio UUID for the mentioned agent (null if unresolved).
 * @param agentMentionContext - ADF content of the block containing the mention.
 * @param agentMentionPrompt - Plain-text fallback prompt for ADF-backed chat input drafts.
 * @param agentMentionParentNodeType - ProseMirror node type of the mention's parent block.
 * @param agentMentionInsertionCount - Monotonic count for agent mention insertions on the page.
 * @param agentMentionName - Display name of the mentioned agent for the nudge title.
 * @param invokedByNodeLocalId - localId of the invoking mention node, seeds the agent-run-state bridge.
 */
export function useAgentMentionNudgeAction(
	agentId?: string | null,
	agentMentionContext?: AgentMentionContext | null,
	agentMentionPrompt?: string | null,
	agentMentionParentNodeType?: string | null,
	agentMentionInsertionCount?: number,
	agentMentionName?: string | null,
	agentIdentityAccountId?: string | null,
	invokedByNodeLocalId?: string | null,
): ConversationAction {
	const resolvedAgentId = agentId ?? null;
	// Convert bare AAID to full IdentityUserAri
	const resolvedAgentIdentityAccountId = agentIdentityAccountId
		? IdentityUserAri.create({ userId: agentIdentityAccountId }).toString()
		: null;
	const resolvedPrompt = agentMentionPrompt ?? '';
	const resolvedPromptAdf = agentMentionContext ?? null;
	const resolvedAgentMentionName = agentMentionName?.trim() || null;
	// With platform_editor_agent_mentions_no_task_autofire on, task-item mentions reach this nudge
	// and draft like every other block (auto-send/seeded-conversation behaviour removed), so treat
	// them as non-task here.
	const isTaskItem =
		agentMentionParentNodeType === TASK_ITEM_NODE_TYPE &&
		!fg('platform_editor_agent_mentions_no_task_autofire');
	const nudgeId =
		agentMentionInsertionCount && agentMentionInsertionCount > 0
			? `${AGENT_MENTION_NUDGE_ID}-${agentMentionInsertionCount}`
			: AGENT_MENTION_NUDGE_ID;
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const { setEditorHadFocusBeforeChatOpen } = useRovoExperienceAPIStore();

	// Keep a ref to the latest prompt so onClick doesn't need it as a dep.
	// agentMentionContext changes on every keystroke; without this, onClick would
	// recreate on each keystroke → cascading memo invalidations → setNudges on every keystroke.
	const resolvedPromptRef = useRef(resolvedPrompt);
	resolvedPromptRef.current = resolvedPrompt;
	const resolvedPromptAdfRef = useRef(resolvedPromptAdf);
	resolvedPromptAdfRef.current = resolvedPromptAdf;

	// Keep a ref to the latest agentId so onClick doesn't capture a stale null.
	// agentId resolves asynchronously; without this, the callback created before
	// resolution would permanently close over null.
	const resolvedAgentIdRef = useRef(resolvedAgentId);
	resolvedAgentIdRef.current = resolvedAgentId;

	const resolvedAgentIdentityAccountIdRef = useRef(resolvedAgentIdentityAccountId);
	resolvedAgentIdentityAccountIdRef.current = resolvedAgentIdentityAccountId;

	const invokedByNodeLocalIdRef = useRef(invokedByNodeLocalId ?? null);
	invokedByNodeLocalIdRef.current = invokedByNodeLocalId ?? null;

	const iconLabel = formatMessage(messages.agentMentionNudgeIconLabel);

	// We capture focus on mousedown so we have the correct pre-click value.
	// The handler is bound directly to the nudge button via the action's onMouseDown prop
	const editorHadFocusAtMouseDownRef = useRef<boolean>(false);
	const onMouseDown = useCallback(() => {
		if (!fg('platform_editor_agent_mentions_drop_one_fixes')) {
			return;
		}
		editorHadFocusAtMouseDownRef.current =
			typeof document !== 'undefined' && isMainEditorContent(document.activeElement);
	}, []);

	const onClick = useCallback(() => {
		// Use the mousedown snapshot — focus has already moved to the nudge button
		// by the time this click handler runs.
		if (fg('platform_editor_agent_mentions_drop_one_fixes')) {
			const editorHadFocus = editorHadFocusAtMouseDownRef.current;
			setEditorHadFocusBeforeChatOpen?.(editorHadFocus);
		}
		publish({
			type: 'chat-new',
			source: 'agentMentionEditorNudge',
			openChatMode: 'mini-modal',
			data: {
				prompt: resolvedPromptAdfRef.current ?? resolvedPromptRef.current,
				// Non-task blocks pre-fill as a draft and skip creating a seeded conversation, so
				// the manual send creates and names the conversation instead of leaving a
				// date-named one. With platform_editor_agent_mentions_no_task_autofire off, task-item
				// mentions never reach this nudge (their presence is gated off in
				// EditPageRovoConfigProvider, so they use the auto-fire path instead). With it on,
				// isTaskItem is false so they draft like every other block.
				// This nudge is only surfaced under the platform_editor_agent_mentions experiment
				// (EditPageRovoConfigProvider gates its presence), so the gate below is effectively
				// double-gated without preconditioning it on the experiment here.
				...(isTaskItem ? {} : { overrideAutoSend: true }),
				...(!isTaskItem && fg('platform_editor_agent_mentions_drop_one_fixes')
					? { skipCreatingSeededConversation: true }
					: {}),
				dialogues: [],
				mode: { useCurrentPageContext: true },
				...(resolvedAgentIdRef.current ? { agentId: resolvedAgentIdRef.current } : {}),
				...(resolvedAgentIdentityAccountIdRef.current
					? { agentIdentityAccountId: resolvedAgentIdentityAccountIdRef.current }
					: {}),
				// Seed the agent-run-state bridge with the invoking node, behind its experiment.
				// Consumed on the manual send via the stash in the pubsub listener.
				...(invokedByNodeLocalIdRef.current &&
				expValEquals('platform_editor_agent_state_bridge', 'isEnabled', true)
					? { invokedByNodeLocalId: invokedByNodeLocalIdRef.current }
					: {}),
			},
		});
	}, [publish, isTaskItem, setEditorHadFocusBeforeChatOpen]);

	return useMemo(() => {
		const title = resolvedAgentMentionName
			? formatMessage(messages.agentMentionNudgeTitleWithAgentName, {
					agentName: resolvedAgentMentionName,
				})
			: formatMessage(messages.agentMentionNudgeTitle);

		const title2 = resolvedAgentMentionName
			? formatMessage(messages.agentMentionNudgeTitleWithAgentName2, {
					agentName: resolvedAgentMentionName,
				})
			: formatMessage(messages.agentMentionNudgeTitle2);

		return {
			id: nudgeId,
			title: fg('platform_editor_agent_mentions_drop_one_fixes') ? title2 : title,
			secondaryTitle: formatMessage(messages.agentMentionNudgeSecondaryTitle),
			icon: () => <AgentMentionIcon label={iconLabel} />,
			onClick,
			onMouseDown,
			type: 'ai-action',
			handleType: HandleActionType.Base,
			nudgeContext: {
				id: AGENT_MENTION_NUDGE_ID,
				selfEligible: true,
				experimentName: 'platform_editor_agent_mentions',
			},
		};
	}, [formatMessage, iconLabel, nudgeId, onClick, onMouseDown, resolvedAgentMentionName]);
}
